import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { useEmailThreads } from '@Hooks/useEmailThreads';
import service from '@Service';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import EmailThread from '@Components/EmailThread';
import { getThreadParticipantName } from '@Utils/emailThread';
import { useStickToBottom } from '@Hooks/useStickToBottom';

export default function EmailThreadPanel({ fishId, className = '' }) {
  const [threadsLoading, setThreadsLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  const [fromInboxId, setFromInboxId] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const client = useQueryClient();

  const scrollRef = useStickToBottom(thread, selectedId);

  const { data: threads, status: threadStatus } = useEmailThreads({
    searchParams: {
      'filter[fishId]': fishId,
      'include':
        'fish.fishProfiles,user,inbox.user,inboundMessages,emailDeliveries.outboundMessage',
    },
    enabled: !!fishId,
  });

  useEffect(() => {
    if (threadStatus === 'success') {
      setThreadsLoading(false);
    }
  }, [threadStatus]);

  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      return;
    }

    const selectedThread = threads?.data?.find((t) => t.id === selectedId);
    setThread(selectedThread);
    setThreadLoading(false);
    setFromInboxId(selectedThread?.inboxId);
  }, [selectedId, threads]);

  useEffect(() => {
    setReplyBody('');
  }, [thread]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!fromInboxId || !replyBody.trim() || !selectedId || !thread?.subject)
      return;

    setSending(true);
    setSendError(null);
    try {
      const result = await service.sendMessage({
        fromInboxId,
        toFishId: thread?.fishId,
        method: 'EMAIL',
        messageBody: replyBody?.trim(),
        messageSubject: thread?.subject?.trim(),
        threadId: thread?.id,
      });

      if (!result.success) {
        throw new Error(result?.error || 'Failed to send email.');
      }

      setReplyBody('');
      // Refresh the thread so the new outbound message appears
      client.invalidateQueries({ queryKey: ['emailThreads'] });
    } catch (err) {
      setSendError(err.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleReply(e);
    }
  };

  return (
    <div
      className={`my-2 flex h-105 min-h-0 rounded-lg border-t border-gray-200 bg-white text-black ${className}`}
    >
      {/* Left: thread list */}
      <div className="flex w-72 shrink-0 flex-col border-r border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
          <h3 className="text-sm font-semibold text-gray-900">Email Threads</h3>
          <span className="text-xs text-gray-400">{threads?.data?.length}</span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {threadsLoading && (
            <div className="p-3 text-sm text-gray-500">Loading threads…</div>
          )}
          {!threadsLoading && threads?.data?.length === 0 && (
            <div className="p-3 text-sm text-gray-500">
              No email threads yet.
            </div>
          )}

          {threads?.data?.map((t) => {
            const isSelected = t.id === selectedId;
            const isUnread = false;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedId(t.id)}
                className={`block w-full border-b border-gray-100 px-3 py-2.5 text-left transition-colors ${
                  isSelected ? 'bg-gray-200' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`truncate text-sm ${
                      isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'
                    }`}
                  >
                    {getThreadParticipantName(t) || t.subject}
                  </span>
                  {isUnread && (
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#CAAB6A]" />
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-gray-500">
                  Subject: {t.subject}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: conversation + reply */}
      <div className="flex min-w-0 flex-1 flex-col">
        {!selectedId && (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
            Select a thread to view the conversation.
          </div>
        )}

        {selectedId && (
          <>
            <div className="border-b border-gray-200 px-4 py-2.5">
              <h4 className="truncate text-sm font-semibold text-gray-900">
                {thread?.subject || '—'}
              </h4>
              {thread?.fish?.id && (
                <p className="text-xs text-gray-400">{`${getThreadParticipantName(thread)} - ${thread?.inbox?.user?.name}`}</p>
              )}
            </div>

            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
            >
              {threadLoading && (
                <div className="text-sm text-gray-400">Loading…</div>
              )}

              <EmailThread thread={thread} />
            </div>

            <form
              onSubmit={handleReply}
              className="border-t border-gray-200 p-2.5"
            >
              {sendError && (
                <p className="mb-1.5 text-xs text-red-600">{sendError}</p>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Write a reply… (⌘+Enter to send)"
                  className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-gray-500 focus:ring-1 focus:ring-gray-500 focus:outline-none"
                />

                <Button
                  type="submit"
                  disabled={sending || !replyBody.trim() || !fromInboxId}
                >
                  {sending ? '…' : 'Send'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
